import { useLocalization } from '@lib/localization';
import { ExpandMore, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, InputAdornment, TextField, Typography
} from '@mui/material';
import { useChangePasswordMutation, useLogoutMutation } from '@store/api/authEndpoints';
import { useState } from 'react';
import { z } from 'zod';

interface ErrorState {
  currentPassword?: string;
  password?: string;
  confirmPassword?: string;
}

const PasswordChangeCard = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [changePassword, { isLoading: isLoadingChangePassword }] = useChangePasswordMutation();
  const [logout, { isLoading: isLoadingLogout }] = useLogoutMutation();

  const { t } = useLocalization();

  const isLoading = isLoadingChangePassword && isLoadingLogout;

  const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters long')
      .refine(
        (password) => {
          const hasUpperCase = /[A-Z]/.test(password);
          const hasLowerCase = /[a-z]/.test(password);
          const hasNumber = /[0-9]/.test(password);
          const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password);
          return hasUpperCase && hasLowerCase && (hasNumber || hasSpecialChar);
        },
        {
          message: 'Password must include at least 1 uppercase, 1 lowercase, and 1 number or special character',
        }
      ),
    confirmPassword: z.string(),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

  const handlePasswordChange = async () => {
    try {
      changePasswordSchema.parse({ currentPassword, newPassword, confirmPassword });

      const result = await changePassword({ currentPassword, newPassword });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});

      if (!result.error) await logout();
    } catch (e) {
      if (e instanceof z.ZodError) {
        const errorMessages: ErrorState = {};

        e.errors.forEach(err => {
          const key = err.path[0] as keyof ErrorState;
          errorMessages[key] = err.message;
        });

        setErrors(errorMessages);
      }
    }
  };

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}
      style={{ borderRadius: '8px', border: 'none', marginBottom: '24px' }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ p: '24px' }}>
          <Typography variant="h6">{t('Settings_ChangePassword')}</Typography>
          <Typography variant="body2">{t('Settings_ChangePassword_Subtitle')}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '24px' }}>
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('Settings_ChangePassword_Current')}
            </Typography>
            <TextField
              disabled={isLoading}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              type="password"
              fullWidth
              size="small"
              variant="outlined"
              sx={{ width: { lg: '400px' } }}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('Settings_ChangePassword_New')}
            </Typography>
            <TextField
              disabled={isLoading}
              error={!!errors.password}
              helperText={errors.password}
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              sx={{ width: { lg: '400px' } }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {t('Settings_ChangePassword_ConfirmNew')}
            </Typography>
            <TextField
              disabled={isLoading}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              type="password"
              sx={{ width: { lg: '400px' } }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ width: { lg: '400px' } }}
            disabled={isLoading}
            onClick={handlePasswordChange}
          >
            {t('Settings_ChangePassword_Update')}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PasswordChangeCard;
